import { StatusBadge } from "../../components/common/StatusBadge";

const money = (value) => `Rs. ${Number(value || 0).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusLabel = (value) => ({
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending Approval",
  CHANGES_REQUESTED: "Changes Requested",
  APPROVED: "Approved",
  PLACED: "Placed",
  RECEIVED: "Received",
  REJECTED: "Rejected",
}[value] || value || "—");

const historyTitle = (event) => {
  if (event.newStatus === "CHANGES_REQUESTED") return "Changes requested";
  if (event.newStatus === "APPROVED") return "Order approved";
  if (event.newStatus === "REJECTED") return "Order rejected";
  if (event.newStatus === "PENDING_APPROVAL" && event.oldStatus === "CHANGES_REQUESTED") return "Resubmitted for approval";
  if (event.newStatus === "PENDING_APPROVAL") return "Submitted for approval";
  if (event.newStatus === "DRAFT") return "Draft saved";
  return statusLabel(event.newStatus);
};

const dotTone = (status) => ({
  DRAFT: "bg-slate-400 ring-slate-100",
  PENDING_APPROVAL: "bg-amber-500 ring-amber-100",
  CHANGES_REQUESTED: "bg-orange-500 ring-orange-100",
  APPROVED: "bg-emerald-600 ring-emerald-100",
  REJECTED: "bg-rose-600 ring-rose-100",
}[status] || "bg-slate-400 ring-slate-100");

export const LiquorOrderItemsTable = ({ items = [] }) => (
  <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-4 py-3">
      <h2 className="font-display font-semibold text-slate-900">Ordered Items</h2>
      <p className="mt-0.5 text-xs text-slate-500">{items.length} {items.length === 1 ? "line item" : "line items"}</p>
    </div>
    <div className="max-w-full overflow-x-auto">
      <table className="w-full min-w-[980px] text-left text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
          <tr>{["Code", "Product", "Measure", "Ex VAT", "VAT", "Unit Total", "Quantity", "Line Total"].map((heading) => <th key={heading} className={`whitespace-nowrap px-3 py-2.5 ${["Ex VAT", "Unit Total", "Line Total"].includes(heading) ? "text-right" : heading === "VAT" || heading === "Quantity" ? "text-center" : "text-left"}`}>{heading}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const lineTotal = item.lineTotal ?? Number(item.priceWithVat || 0) * Number(item.quantity || 0);
            return <tr key={item.id || item.catalogItemId} className="border-t border-slate-100">
              <td className="whitespace-nowrap px-3 py-3 text-slate-600">{item.supplierProductCode || item.productCode || "—"}</td>
              <td className="px-3 py-3 font-medium text-slate-900">{item.productName}</td>
              <td className="whitespace-nowrap px-3 py-3 text-slate-600">{item.measure}</td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">{money(item.priceExVat)}</td>
              <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums">{item.vatPercentage}%</td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">{money(item.priceWithVat)}</td>
              <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums">{item.quantity}</td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums">{money(lineTotal)}</td>
            </tr>;
          })}
          {!items.length && <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-slate-500">No items in this order.</td></tr>}
        </tbody>
      </table>
    </div>
  </section>
);

export const LiquorOrderSummary = ({ subtotal, vat, total, itemCount }) => (
  <aside className="min-w-0 self-start rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <h2 className="font-display text-base font-semibold text-slate-900">Order Summary</h2>
      {itemCount !== undefined && <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{itemCount} {itemCount === 1 ? "Item" : "Items"}</span>}
    </div>
    <dl className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 border-t border-slate-100 pt-4 text-sm">
      <dt className="text-slate-600">Subtotal Ex VAT</dt><dd className="whitespace-nowrap text-right tabular-nums text-slate-800">{money(subtotal)}</dd>
      <dt className="text-slate-600">VAT</dt><dd className="whitespace-nowrap text-right tabular-nums text-slate-800">{money(vat)}</dd>
    </dl>
    <div className="mt-4 flex min-w-0 items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-3">
      <span className="font-semibold text-slate-900">Grand Total</span><span className="whitespace-nowrap text-lg font-bold tabular-nums text-navy-900">{money(total)}</span>
    </div>
  </aside>
);

export const LiquorOrderHistoryTimeline = ({ history = [] }) => (
  <section className="mt-4 min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <h2 className="font-display font-semibold text-slate-900">Status History</h2>
    {history.length ? <ol className="mt-4">
      {history.map((event, index) => <li key={`${event.createdAt}-${index}`} className="relative min-w-0 pb-4 pl-7 last:pb-0 before:absolute before:bottom-0 before:left-[5px] before:top-3 before:w-px before:bg-slate-200 last:before:hidden">
        <span className={`absolute left-0 top-1 h-3 w-3 rounded-full ring-4 ${dotTone(event.newStatus)}`} />
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="break-words text-sm font-semibold text-slate-900">{historyTitle(event)}</h3>
            <StatusBadge value={event.newStatus} />
          </div>
          <p className="mt-0.5 break-words text-xs text-slate-500">{statusLabel(event.oldStatus)} → {statusLabel(event.newStatus)}</p>
          <p className="mt-1 break-words text-sm text-slate-700">{event.changedBy}</p>
          <time className="mt-0.5 block text-xs text-slate-500">{new Date(event.createdAt).toLocaleString("en-LK", { dateStyle: "medium", timeStyle: "short" })}</time>
          {event.note && <p className="mt-2 break-words rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"><span className="font-medium">Note: </span>{event.note}</p>}
        </div>
      </li>)}
    </ol> : <p className="mt-2 text-sm text-slate-500">No status changes recorded.</p>}
  </section>
);

export const SupplierDeliveryHistory = ({ history = [] }) => {
  const newestFirst = [...history].sort((a, b) => new Date(b.createdAt || b.sentAt || b.attemptedAt || 0) - new Date(a.createdAt || a.sentAt || a.attemptedAt || 0));
  return <section className="mt-4 min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <h2 className="font-display font-semibold text-slate-900">Supplier Delivery History</h2>
    {newestFirst.length ? <ol className="mt-3 divide-y divide-slate-100">{newestFirst.map((attempt, index) => {
      const sent = ["SENT", "SUCCESS", "SUCCEEDED"].includes(String(attempt.status || attempt.deliveryStatus || "").toUpperCase());
      const contact = attempt.contact || attempt.supplierDeliveryContact || {};
      return <li key={attempt.id || `${attempt.createdAt || attempt.sentAt || attempt.attemptedAt}-${index}`} className="py-3 first:pt-0 last:pb-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${sent ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>{sent ? "Sent" : "Failed"}</span><time className="text-xs text-slate-500">{new Date(attempt.createdAt || attempt.sentAt || attempt.attemptedAt).toLocaleString("en-LK", { dateStyle: "medium", timeStyle: "short" })}</time></div>
        <p className="mt-1 break-words text-sm text-slate-800">{contact.name || attempt.contactName || "Supplier contact"}{(contact.whatsappNumber || attempt.whatsappNumber || attempt.contactWhatsappNumber || attempt.contactNumber) && ` · ${contact.whatsappNumber || attempt.whatsappNumber || attempt.contactWhatsappNumber || attempt.contactNumber}`}</p>
        {(attempt.sentBy || attempt.sentByName || attempt.createdBy) && <p className="mt-0.5 text-xs text-slate-500">{sent ? "Sent by" : "Attempted by"} {attempt.sentBy || attempt.sentByName || attempt.createdBy}</p>}
        {!sent && (attempt.reason || attempt.failureReason) && <p className="mt-1 break-words text-sm text-rose-700">Reason: {attempt.reason || attempt.failureReason}</p>}
      </li>;
    })}</ol> : <p className="mt-2 text-sm text-slate-500">No supplier delivery attempts.</p>}
  </section>;
};
